import { supabase } from '@/lib/supabase'
import type {
  OrganizationProfileFormData,
  OrganizationProfileRow,
} from '@/types/organization-profile'

/**
 * Fetch organization profile for an account.
 * If it doesn't exist, creates an empty one.
 */
export async function fetchOrganizationProfile(
  organizationId: string
): Promise<OrganizationProfileRow> {
  // First, try to fetch existing profile
  const { data, error } = await supabase
    .from('organization_profiles')
    .select('*')
    .eq('organization_id', organizationId)
    .single()

  // If profile exists, return it
  if (data) {
    return data
  }

  // If not found (404), create a new one
  if (error?.code === 'PGRST116') {
    const { data: newProfile, error: createError } = await supabase
      .from('organization_profiles')
      .insert({
        organization_id: organizationId,
      })
      .select()
      .single()

    if (createError) throw createError
    return newProfile
  }

  // Re-throw other errors
  if (error) throw error

  throw new Error('Failed to fetch organization profile')
}

/**
 * Update an existing organization profile
 */
export async function updateOrganizationProfile(
  id: string,
  formData: Partial<OrganizationProfileFormData>
): Promise<OrganizationProfileRow> {
  // Build update object, only including fields that were provided
  const updateData: Record<string, unknown> = {}

  if (formData.legal_name !== undefined) updateData.legal_name = formData.legal_name || null
  if (formData.tax_id !== undefined) updateData.tax_id = formData.tax_id || null
  if (formData.industry !== undefined) updateData.industry = formData.industry || null
  if (formData.timezone !== undefined) updateData.timezone = formData.timezone || null
  if (formData.currency !== undefined) updateData.currency = formData.currency || null
  if (formData.logo_url !== undefined) updateData.logo_url = formData.logo_url || null
  if (formData.primary_color !== undefined)
    updateData.primary_color = formData.primary_color || null
  if (formData.primary_phone !== undefined)
    updateData.primary_phone = formData.primary_phone || null
  if (formData.primary_email !== undefined)
    updateData.primary_email = formData.primary_email || null
  if (formData.website_url !== undefined) updateData.website_url = formData.website_url || null
  if (formData.address_line1 !== undefined)
    updateData.address_line1 = formData.address_line1 || null
  if (formData.city !== undefined) updateData.city = formData.city || null
  if (formData.state_province !== undefined)
    updateData.state_province = formData.state_province || null
  if (formData.postal_code !== undefined)
    updateData.postal_code = formData.postal_code || null
  if (formData.country !== undefined) updateData.country = formData.country || null

  const { data, error } = await supabase
    .from('organization_profiles')
    .update(updateData)
    .eq('id', id)
    .select()
    .single()

  if (error) throw error
  return data
}
